import { ApplicationContext } from '@wix/editor-platform-contexts';
import { createHostModule } from '@wix/sdk-runtime/host-modules';
import { Host } from '@wix/sdk-types';

interface IHostWithPlatformContexts {
  applicationContext?: Promise<ApplicationContext>;
}

type MethodNames<T> = Extract<
  {
    [K in keyof T]: T[K] extends (...args: any[]) => any ? K : never;
  }[keyof T],
  string
>;

type PickMethods<T, Keys extends keyof T> = {
  [K in Keys]: T[K];
};

export class PlatformSDKShape<
  TShape extends new (...args: any[]) => any,
  TSelected extends MethodNames<InstanceType<TShape>> = never,
> {
  constructor(
    private ShapeConstructor: TShape,
    private selectedMethods: TSelected[] = [],
  ) {}

  withPublicMethod<K extends MethodNames<InstanceType<TShape>>>(
    method: K,
  ): PlatformSDKShape<TShape, TSelected | K> {
    return new PlatformSDKShape(this.ShapeConstructor, [
      ...this.selectedMethods,
      method,
    ]);
  }

  build() {
    if (!this.selectedMethods.length) {
      throw new Error('methods are not defined for the SDK shape');
    }

    const sdk = this.selectedMethods.reduce(
      (acc, method) => {
        return {
          ...acc,
          [method]: (host: IHostWithPlatformContexts) => {
            const instance = new this.ShapeConstructor(
              host?.applicationContext,
            );

            if (!instance[method]) {
              return () => {
                console.error(`"${method}" is not defined in the SDK shape`);
              };
            }

            return instance[method].bind(instance);
          },
        };
      },
      {} as PickMethods<InstanceType<TShape>, TSelected>,
    );

    // @ts-expect-error
    return createHostModule<Host, InstanceType<TShape>>(sdk);
  }
}
